/**
 * Frontend Logger — lightweight façade that writes to useLogStore.
 *
 * Use this instead of bare `console.*` calls throughout editor-core.
 * In dev mode the entries are also mirrored to the browser console
 * (handled by the store's `log()` method).
 *
 * Usage:
 *   import { frontendLog } from "../utils/frontend-logger";
 *   frontendLog.error("something failed", err);
 *   frontendLog.debug("ws connected", { source: "transport" });
 */
import { useLogStore, type LogSource } from "../store/log-store";

function fmt(msg: string, extra?: unknown): string {
    if (extra === undefined) return msg;
    if (extra instanceof Error) return `${msg} ${extra.message}`;
    if (typeof extra === "string") return `${msg} ${extra}`;
    try {
        return `${msg} ${JSON.stringify(extra)}`;
    } catch {
        return `${msg} ${String(extra)}`;
    }
}

interface LogOptions {
    source?: LogSource;
    data?: unknown;
}

function log(level: "debug" | "info" | "warn" | "error", msg: string, extra?: unknown, opts?: LogOptions) {
    const message = fmt(msg, extra);
    useLogStore.getState().log(level, message, {
        source: opts?.source ?? "system",
        data: opts?.data,
        stack: extra instanceof Error ? extra.stack : undefined,
    });
}

export const frontendLog = {
    debug: (msg: string, extra?: unknown, opts?: LogOptions) => log("debug", msg, extra, opts),
    info: (msg: string, extra?: unknown, opts?: LogOptions) => log("info", msg, extra, opts),
    warn: (msg: string, extra?: unknown, opts?: LogOptions) => log("warn", msg, extra, opts),
    error: (msg: string, extra?: unknown, opts?: LogOptions) => log("error", msg, extra, opts),
};
